import React from "react";
import "./TimeRangeFilter.css";

export type TimeRangeKey =
  | "last30"
  | "last90"
  | "ytd"
  | "all"
  | "custom";

export type TimeRangeValue = {
  key: TimeRangeKey;
  /** ISO date (YYYY-MM-DD) or null for open-ended. */
  from: string | null;
  to: string | null;
};

const PRESETS: Array<{ key: TimeRangeKey; label: string }> = [
  { key: "last30", label: "Last 30 days" },
  { key: "last90", label: "Last 90 days" },
  { key: "ytd",    label: "This year" },
  { key: "all",    label: "All time" },
  { key: "custom", label: "Custom…" },
];

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Resolve a preset key to concrete from/to dates.
 * "custom" resolves to nulls — caller supplies its own range.
 * "all" resolves to nulls too (open-ended on both sides).
 */
export function resolveTimeRange(key: TimeRangeKey, customFrom?: string, customTo?: string): TimeRangeValue {
  const today = new Date();
  const offset = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  switch (key) {
    case "last30":
      return { key, from: iso(offset(30)), to: iso(today) };
    case "last90":
      return { key, from: iso(offset(90)), to: iso(today) };
    case "ytd":
      return { key, from: `${today.getFullYear()}-01-01`, to: iso(today) };
    case "all":
      return { key, from: null, to: null };
    case "custom":
      return { key, from: customFrom || null, to: customTo || null };
  }
}

type Props = {
  value: TimeRangeValue;
  onChange: (next: TimeRangeValue) => void;
};

export function TimeRangeFilter({ value, onChange }: Props) {
  function handlePreset(key: TimeRangeKey) {
    if (key === "custom") {
      const next = resolveTimeRange("custom", value.from || undefined, value.to || undefined);
      onChange(next);
    } else {
      onChange(resolveTimeRange(key));
    }
  }

  function handleCustomFrom(v: string) {
    onChange({ key: "custom", from: v || null, to: value.to });
  }
  function handleCustomTo(v: string) {
    onChange({ key: "custom", from: value.from, to: v || null });
  }

  return (
    <div className="oht-trf">
      <div className="oht-trf-chips">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`oht-trf-chip${value.key === p.key ? " oht-trf-chip--active" : ""}`}
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      {value.key === "custom" ? (
        <div className="oht-trf-custom">
          <label className="oht-trf-label">
            From
            <input
              type="date"
              value={value.from || ""}
              onChange={(e) => handleCustomFrom(e.target.value)}
            />
          </label>
          <label className="oht-trf-label">
            To
            <input
              type="date"
              value={value.to || ""}
              onChange={(e) => handleCustomTo(e.target.value)}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}
